import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const FOLLOW_UP_LABELS = {
  active: 'Devam Ediyor',
  approaching: 'Süre Yaklaşıyor',
  expired: 'Süresi Doldu',
};

const FOLLOW_UP_FILL = {
  active: 'FFD1FAE5',
  approaching: 'FFFEF3C7',
  expired: 'FFFFE4E6',
};

const thinBorder = {
  top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
  left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
  bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
  right: { style: 'thin', color: { argb: 'FFE2E8F0' } },
};

export const VEHICLE_IMPORT_COLUMNS = [
  { header: 'Araç ID*', key: 'vehicle_code', width: 12 },
  { header: 'Plaka*', key: 'license_plate', width: 14 },
  { header: 'Tesis ID', key: 'hotel_id', width: 10 },
  { header: 'Tesis Adı', key: 'hotel_name', width: 24 },
  { header: 'Şehir', key: 'city', width: 12 },
  { header: 'Pozisyon', key: 'position', width: 18 },
  { header: 'Zimmetli', key: 'assigned_person', width: 20 },
  { header: 'Araç Tipi', key: 'vehicle_type', width: 14 },
  { header: 'Taşıt Türü', key: 'transport_type', width: 14 },
  { header: 'Marka', key: 'brand', width: 14 },
  { header: 'Model', key: 'model', width: 16 },
  { header: 'Paket', key: 'package_name', width: 14 },
  { header: 'Vites', key: 'transmission', width: 10 },
  { header: 'Yakıt', key: 'fuel_type', width: 10 },
  { header: 'Model Yılı', key: 'model_year', width: 11 },
  { header: 'Ruhsat No', key: 'license_number', width: 14 },
  { header: 'Tescil Tarihi', key: 'registration_date', width: 14 },
  { header: 'Motor No', key: 'engine_number', width: 16 },
  { header: 'Şasi No', key: 'chassis_number', width: 18 },
  { header: 'Trafik Poliçe', key: 'traffic_policy_date', width: 14 },
  { header: 'Sigorta Poliçe', key: 'insurance_policy_date', width: 14 },
  { header: 'KM', key: 'current_km', width: 10 },
  { header: 'Durum', key: 'is_active', width: 10 },
];

const formatPlateDisplay = (plate) => {
  if (!plate) return '';
  const raw = String(plate).toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (!raw) return String(plate);

  let index = 0;
  let city = '';
  while (index < raw.length && city.length < 2 && /\d/.test(raw[index])) {
    city += raw[index];
    index += 1;
  }

  let letters = '';
  while (index < raw.length && letters.length < 3 && /[A-Z]/.test(raw[index])) {
    letters += raw[index];
    index += 1;
  }

  const tail = raw.slice(index).replace(/\D/g, '').slice(0, 4);
  return [city, letters, tail].filter(Boolean).join(' ');
};

const statusLabel = (status) => FOLLOW_UP_LABELS[status] || '—';

const styleStatusCell = (cell, status) => {
  const fill = FOLLOW_UP_FILL[status];
  if (fill) {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
  }
};

const styleHeaderRow = (worksheet) => {
  const headerRow = worksheet.getRow(1);
  headerRow.height = 24;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D9488' } };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = thinBorder;
  });
};

const toIsoDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const trMatch = text.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (trMatch) {
    const [, d, m, y] = trMatch;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return null;
};

const excelSerialToIso = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const utc = new Date(Math.round((value - 25569) * 86400 * 1000));
  if (Number.isNaN(utc.getTime())) return null;
  return utc.toISOString().slice(0, 10);
};

const normalizeDateCell = (value) => {
  if (value instanceof Date) return toIsoDate(value);
  if (typeof value === 'number') return excelSerialToIso(value);
  return toIsoDate(value);
};

const normalizeActive = (value) => {
  if (value === null || value === undefined || value === '') return true;
  const text = String(value).trim().toLocaleLowerCase('tr-TR');
  if (['pasif', 'false', '0', 'hayır', 'hayir', 'no'].includes(text)) return false;
  return true;
};

const cellText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object' && value.text) return String(value.text).trim();
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value).trim();
};

const normalizeHeader = (value) =>
  String(value || '')
    .replace(/\*/g, '')
    .trim()
    .toLocaleLowerCase('tr-TR');

const HEADER_ALIASES = {
  'araç id': 'vehicle_code',
  'arac id': 'vehicle_code',
  vehicle_code: 'vehicle_code',
  plaka: 'license_plate',
  license_plate: 'license_plate',
  'tesis id': 'hotel_id',
  hotel_id: 'hotel_id',
  'tesis adı': 'hotel_name',
  'tesis adi': 'hotel_name',
  hotel_name: 'hotel_name',
  şehir: 'city',
  sehir: 'city',
  city: 'city',
  pozisyon: 'position',
  position: 'position',
  zimmetli: 'assigned_person',
  assigned_person: 'assigned_person',
  'araç tipi': 'vehicle_type',
  'arac tipi': 'vehicle_type',
  vehicle_type: 'vehicle_type',
  'taşıt türü': 'transport_type',
  'tasit turu': 'transport_type',
  transport_type: 'transport_type',
  marka: 'brand',
  brand: 'brand',
  model: 'model',
  paket: 'package_name',
  package_name: 'package_name',
  vites: 'transmission',
  transmission: 'transmission',
  yakıt: 'fuel_type',
  yakit: 'fuel_type',
  fuel_type: 'fuel_type',
  'model yılı': 'model_year',
  'model yili': 'model_year',
  model_year: 'model_year',
  'ruhsat no': 'license_number',
  license_number: 'license_number',
  'tescil tarihi': 'registration_date',
  registration_date: 'registration_date',
  'motor no': 'engine_number',
  engine_number: 'engine_number',
  'şasi no': 'chassis_number',
  'sasi no': 'chassis_number',
  chassis_number: 'chassis_number',
  'trafik poliçe': 'traffic_policy_date',
  'trafik police': 'traffic_policy_date',
  traffic_policy_date: 'traffic_policy_date',
  'sigorta poliçe': 'insurance_policy_date',
  'sigorta police': 'insurance_policy_date',
  insurance_policy_date: 'insurance_policy_date',
  km: 'current_km',
  current_km: 'current_km',
  durum: 'is_active',
  is_active: 'is_active',
};

export async function exportVehiclesToExcel(rows, filenamePrefix = 'arac-kayitlari') {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'HelpDesk';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet('Araç Kayıtları', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  worksheet.columns = [
    { header: 'Araç ID', key: 'vehicle_code', width: 10 },
    { header: 'Şehir', key: 'city', width: 12 },
    { header: 'Tesis', key: 'hotel_name', width: 24 },
    { header: 'Plaka', key: 'license_plate', width: 14 },
    { header: 'Marka', key: 'brand', width: 14 },
    { header: 'Model', key: 'model', width: 18 },
    { header: 'Yakıt', key: 'fuel_type', width: 10 },
    { header: 'Model Yılı', key: 'model_year', width: 11 },
    { header: 'KM', key: 'current_km', width: 12 },
    { header: 'Zimmetli', key: 'assigned_person', width: 22 },
    { header: 'Pozisyon', key: 'position', width: 20 },
    { header: 'Trafik', key: 'traffic', width: 16 },
    { header: 'Sigorta', key: 'insurance', width: 16 },
    { header: 'Muayene', key: 'inspection', width: 16 },
    { header: 'Durum', key: 'is_active', width: 10 },
  ];

  styleHeaderRow(worksheet);

  rows.forEach((row, index) => {
    const excelRow = worksheet.addRow({
      vehicle_code: row.vehicle_code || '',
      city: row.city || '',
      hotel_name: row.hotel_name || '',
      license_plate: formatPlateDisplay(row.license_plate),
      brand: row.brand || '',
      model: row.model || '',
      fuel_type: row.fuel_type || '',
      model_year: row.model_year ?? '',
      current_km: row.current_km != null && row.current_km !== '' ? Number(row.current_km) : '',
      assigned_person: row.assigned_person || '',
      position: row.position || '',
      traffic: statusLabel(row.traffic_follow_up_status),
      insurance: statusLabel(row.insurance_follow_up_status),
      inspection: statusLabel(row.inspection_follow_up_status),
      is_active: row.is_active === false ? 'Pasif' : 'Aktif',
    });

    excelRow.height = 20;
    excelRow.eachCell((cell) => {
      cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
      cell.border = thinBorder;
      if (index % 2 === 1) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0FDFA' } };
      }
    });

    const kmCell = excelRow.getCell('current_km');
    if (typeof kmCell.value === 'number') {
      kmCell.numFmt = '#,##0';
      kmCell.alignment = { vertical: 'middle', horizontal: 'right' };
    }

    styleStatusCell(excelRow.getCell('traffic'), row.traffic_follow_up_status);
    styleStatusCell(excelRow.getCell('insurance'), row.insurance_follow_up_status);
    styleStatusCell(excelRow.getCell('inspection'), row.inspection_follow_up_status);
  });

  if (worksheet.rowCount > 1) {
    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: worksheet.rowCount, column: worksheet.columnCount },
    };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, `${filenamePrefix}-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export async function downloadVehicleImportTemplate(filenamePrefix = 'arac-ice-aktarim-sablonu') {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'HelpDesk';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet('İçe Aktarım', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });
  worksheet.columns = VEHICLE_IMPORT_COLUMNS;
  styleHeaderRow(worksheet);

  worksheet.addRow({
    vehicle_code: 'ORN-001',
    license_plate: '07 ABC 07',
    hotel_id: '',
    hotel_name: 'Örnek Tesis',
    city: 'Antalya',
    position: '',
    assigned_person: '',
    vehicle_type: '',
    transport_type: '',
    brand: 'Toyota',
    model: 'Corolla',
    package_name: '',
    transmission: 'Otomatik',
    fuel_type: 'Benzin',
    model_year: 2022,
    license_number: '',
    registration_date: '2022-01-15',
    engine_number: '',
    chassis_number: '',
    traffic_policy_date: '2026-01-15',
    insurance_policy_date: '2026-01-15',
    current_km: 15000,
    is_active: 'Aktif',
  });

  const noteSheet = workbook.addWorksheet('Açıklama');
  noteSheet.getColumn(1).width = 90;
  [
    'Zorunlu alanlar: Araç ID, Plaka.',
    'Tesis: Tesis ID veya Tesis Adı alanlarından en az birini doldurun (kullanıcının oteli yoksa zorunlu).',
    'Tarih formatı: YYYY-MM-DD veya GG.AA.YYYY',
    'Durum: Aktif / Pasif (boş bırakılırsa Aktif kabul edilir).',
    'Örnek satırı silip kendi kayıtlarınızı ekleyin.',
  ].forEach((line, index) => {
    noteSheet.getCell(index + 1, 1).value = line;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, `${filenamePrefix}-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export async function parseVehicleImportFile(file) {
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const worksheet =
    workbook.getWorksheet('İçe Aktarım') ||
    workbook.worksheets.find((sheet) => sheet.name !== 'Açıklama') ||
    workbook.worksheets[0];

  if (!worksheet) {
    throw new Error('Excel dosyasında sayfa bulunamadı.');
  }

  const headerRow = worksheet.getRow(1);
  const columnMap = {};
  headerRow.eachCell((cell, colNumber) => {
    const key = HEADER_ALIASES[normalizeHeader(cell.value)];
    if (key) columnMap[colNumber] = key;
  });

  if (!Object.values(columnMap).includes('vehicle_code') || !Object.values(columnMap).includes('license_plate')) {
    throw new Error('Şablonda Araç ID ve Plaka sütunları bulunamadı.');
  }

  const rows = [];
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    const raw = {};
    Object.entries(columnMap).forEach(([col, key]) => {
      raw[key] = row.getCell(Number(col)).value;
    });

    const vehicleCode = cellText(raw.vehicle_code);
    const licensePlate = cellText(raw.license_plate);
    if (!vehicleCode && !licensePlate) return;

    rows.push({
      vehicle_code: vehicleCode,
      license_plate: licensePlate,
      hotel_id: cellText(raw.hotel_id),
      hotel_name: cellText(raw.hotel_name),
      city: cellText(raw.city),
      position: cellText(raw.position),
      assigned_person: cellText(raw.assigned_person),
      vehicle_type: cellText(raw.vehicle_type),
      transport_type: cellText(raw.transport_type),
      brand: cellText(raw.brand),
      model: cellText(raw.model),
      package_name: cellText(raw.package_name),
      transmission: cellText(raw.transmission),
      fuel_type: cellText(raw.fuel_type),
      model_year: cellText(raw.model_year) ? Number(cellText(raw.model_year)) : null,
      license_number: cellText(raw.license_number),
      registration_date: normalizeDateCell(raw.registration_date),
      engine_number: cellText(raw.engine_number),
      chassis_number: cellText(raw.chassis_number),
      traffic_policy_date: normalizeDateCell(raw.traffic_policy_date),
      insurance_policy_date: normalizeDateCell(raw.insurance_policy_date),
      current_km: cellText(raw.current_km) ? Number(cellText(raw.current_km)) : null,
      is_active: normalizeActive(raw.is_active),
      _rowNumber: rowNumber,
    });
  });

  return rows;
}

export function resolveImportHotelId(row, hotels = [], fallbackHotelId = null) {
  if (row.hotel_id) {
    const byId = hotels.find((hotel) => String(hotel.id) === String(row.hotel_id));
    if (byId) return String(byId.id);
    if (/^\d+$/.test(String(row.hotel_id))) return String(row.hotel_id);
  }
  if (row.hotel_name) {
    const name = row.hotel_name.toLocaleLowerCase('tr-TR');
    const byName = hotels.find(
      (hotel) => String(hotel.hotel_name || '').toLocaleLowerCase('tr-TR') === name,
    );
    if (byName) return String(byName.id);
  }
  if (fallbackHotelId) return String(fallbackHotelId);
  return '';
}

export function printVehicleSummary(vehiclesInput) {
  const vehicles = (Array.isArray(vehiclesInput) ? vehiclesInput : [vehiclesInput]).filter(Boolean);
  if (!vehicles.length) return;

  const followUp = (status) => FOLLOW_UP_LABELS[status] || '—';
  const val = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

  const buildRows = (vehicle) => [
    ['Araç ID', val(vehicle.vehicle_code)],
    ['Plaka', formatPlateDisplay(vehicle.license_plate) || val(vehicle.license_plate)],
    ['Şehir', val(vehicle.city)],
    ['Tesis', val(vehicle.hotel_name)],
    ['Marka / Model', `${val(vehicle.brand)} / ${val(vehicle.model)}`],
    ['Yakıt', val(vehicle.fuel_type)],
    ['Model yılı', val(vehicle.model_year)],
    ['KM', val(vehicle.current_km)],
    ['Zimmetli', val(vehicle.assigned_person)],
    ['Pozisyon', val(vehicle.position)],
    ['Trafik poliçe', val(vehicle.traffic_policy_date)],
    ['Sigorta poliçe', val(vehicle.insurance_policy_date)],
    ['Muayene', val(vehicle.first_inspection_date)],
    ['Trafik durumu', followUp(vehicle.traffic_follow_up_status)],
    ['Sigorta durumu', followUp(vehicle.insurance_follow_up_status)],
    ['Muayene durumu', followUp(vehicle.inspection_follow_up_status)],
    ['Durum', vehicle.is_active === false ? 'Pasif' : 'Aktif'],
  ];

  const sections = vehicles
    .map((vehicle, index) => {
      const rows = buildRows(vehicle);
      return `
        <section class="vehicle-block${index > 0 ? ' page-break' : ''}">
          <h1>Araç kayıt özeti</h1>
          <div class="sub">${val(vehicle.license_plate)} · ${new Date().toLocaleString('tr-TR')}</div>
          <table>
            ${rows.map(([k, v]) => `<tr><th>${k}</th><td>${v}</td></tr>`).join('')}
          </table>
        </section>
      `;
    })
    .join('');

  const titlePlate = vehicles.length === 1
    ? val(vehicles[0].license_plate)
    : `${vehicles.length} araç`;

  const html = `<!DOCTYPE html>
<html lang="tr">
<head>
  <meta charset="utf-8" />
  <title>Araç Özeti - ${titlePlate}</title>
  <style>
    body { font-family: Segoe UI, Arial, sans-serif; color: #0f172a; margin: 24px; }
    h1 { font-size: 18px; margin: 0 0 4px; }
    .sub { color: #64748b; font-size: 12px; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
    th, td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; font-size: 13px; }
    th { width: 34%; background: #f8fafc; }
    .page-break { page-break-before: always; margin-top: 24px; }
    @media print { body { margin: 12mm; } .page-break { margin-top: 0; } }
  </style>
</head>
<body>
  ${sections}
</body>
</html>`;

  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (printWindow) {
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    const runPrint = () => {
      try {
        printWindow.focus();
        printWindow.print();
      } catch {
        // ignore
      }
    };
    if (printWindow.document.readyState === 'complete') {
      setTimeout(runPrint, 150);
    } else {
      printWindow.onload = () => setTimeout(runPrint, 150);
    }
    return;
  }

  // Pop-up engelliyse gizli (ama boyutlu) iframe ile dene
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'print-frame');
  iframe.setAttribute('aria-hidden', 'true');
  Object.assign(iframe.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '800px',
    height: '1000px',
    border: '0',
    opacity: '0',
    pointerEvents: 'none',
    zIndex: '-1',
  });
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const frameDocument = frameWindow?.document;
  if (!frameWindow || !frameDocument) {
    iframe.remove();
    throw new Error('Yazdırma alanı oluşturulamadı. Tarayıcı pop-up engelini kontrol edin.');
  }

  frameDocument.open();
  frameDocument.write(html);
  frameDocument.close();

  const cleanup = () => setTimeout(() => iframe.remove(), 1000);
  const triggerPrint = () => {
    try {
      frameWindow.focus();
      frameWindow.print();
    } finally {
      if (frameWindow.matchMedia) {
        const media = frameWindow.matchMedia('print');
        const onChange = (e) => {
          if (!e.matches) {
            media.removeEventListener?.('change', onChange);
            cleanup();
          }
        };
        media.addEventListener?.('change', onChange);
      }
      frameWindow.onafterprint = cleanup;
      setTimeout(cleanup, 2000);
    }
  };

  setTimeout(triggerPrint, 200);
}
